import React, { useState, useEffect, useRef } from 'react';
import SwipeGallery from './SwipeGallery';
import ReviewCard from './ReviewCard';
import { FEATURED_REVIEWS } from '../data/featuredReviews';
import { GOOGLE_REVIEWS } from '../data/googleReviews';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';

export default function Results() {
  const images = [
    '/assets/results1.jpeg',
    '/assets/results2.jpeg',
    '/assets/results3.jpeg',
    '/assets/results4.jpeg',
    '/assets/results5.jpeg',
    '/assets/results6.jpeg',
    '/assets/results7.jpeg',
    '/assets/results8.jpeg',
    '/assets/results9.jpeg',
  ];

  // All testimonials render as real HTML cards: the six original reviews
  // (formerly static PNGs) followed by the Google reviews.
  const reviewCards = [...FEATURED_REVIEWS, ...GOOGLE_REVIEWS];

  // Before/after video. The .MOV cannot be decoded by Alpine's open-source
  // Chromium, so when react-snap loads the page the media request never
  // completes and its networkidle0 wait hangs the whole prerender. We detect
  // the ReactSnap user agent and skip rendering the <video> entirely during
  // prerender (an absent element opens no connection); real visitors get the
  // video, autoplayed from the effect below. preload="none" is kept as a
  // belt-and-suspenders for clients.
  const isPrerender =
    typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
  const videoRef = useRef(null);
  useEffect(() => {
    if (isPrerender) return;
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const played = video.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  }, [isPrerender]);

  // Lightbox state
  const [isOpen, setIsOpen] = useState(false);
  const [active, setActive] = useState(0);

  const openLightbox = (i) => {
    setActive(i);
    setIsOpen(true);
  };
  const closeLightbox = () => setIsOpen(false);
  const lightboxNext = () => setActive((a) => (a + 1) % images.length);
  const lightboxPrev = () => setActive((a) => (a - 1 + images.length) % images.length);

  // Keyboard controls for the results lightbox
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') lightboxNext();
      if (e.key === 'ArrowLeft') lightboxPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Lock body scroll when the lightbox is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = isOpen ? 'hidden' : prev || '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <section className='am-results-section am-use-dom-waves'>
      {/* Top wave (DOM-rendered to avoid mobile hairline seams) */}
      <div className='am-wave am-wave-top' aria-hidden>
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 120' preserveAspectRatio='none'>
          <path fill='#ffffff' d='M0,72 C360,30 720,120 1440,72 L1440,0 L0,0 Z' />
        </svg>
      </div>
      <div className='am-container am-results-inner'>
        <h2 className='am-results-title'>Check Out Our Results</h2>

        <div className='am-results-main'>
          <Swiper
            modules={[Navigation, Keyboard]}
            navigation
            keyboard={{ enabled: true }}
            loop={true}
            slidesPerView={3}
            slidesPerGroup={3}
            spaceBetween={12}
            breakpoints={{
              0: { slidesPerView: 1, slidesPerGroup: 1 },
              720: { slidesPerView: 2, slidesPerGroup: 2 },
              920: { slidesPerView: 3, slidesPerGroup: 3 },
            }}
            className='am-results-swiper-main'
          >
            {images.map((src, i) => (
              <SwiperSlide key={src}>
                <div
                  className='am-results-main-image'
                  onClick={() => openLightbox(i)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') openLightbox(i);
                  }}
                >
                  <img src={src} alt={`AM Facilities cleaning result ${i + 1}`} />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Lightbox overlay */}
        {isOpen && (
          <div className='am-lightbox' role='dialog' aria-modal='true'>
            <button className='am-lightbox-close' onClick={closeLightbox} aria-label='Close'>
              ×
            </button>
            <button className='am-lightbox-prev' onClick={lightboxPrev} aria-label='Previous'>
              ‹
            </button>
            <div className='am-lightbox-content'>
              <img src={images[active]} alt={`AM Facilities cleaning result ${active + 1}, enlarged`} />
            </div>
            <button className='am-lightbox-next' onClick={lightboxNext} aria-label='Next'>
              ›
            </button>
          </div>
        )}

        <div className='am-results-cta'>
          <button className='am-chip am-chip-primary' data-open-quote>I want some results like this!</button>
        </div>

        <h2 className='am-results-title'>Before and After</h2>
        <div className='am-before-after'>
          <div className='am-before-after-video'>
            {!isPrerender && (
              <video
                ref={videoRef}
                src='/assets/before_after.MOV'
                muted
                loop
                playsInline
                preload='none'
                controls={false}
              />
            )}
          </div>
        </div>

        <div className='am-results-cta'>
          <button className='am-chip am-chip-primary' data-open-quote>I want some results like this!</button>
        </div>

        <h2 className='am-results-title'>See why our customers love us!</h2>

        <div className='am-testimonials-row'>
          <SwipeGallery
            items={reviewCards}
            slidesPerView={3}
            spaceBetween={18}
            className='am-testimonials-swiper'
            renderItem={(review, i) => <ReviewCard key={`${review.name}-${i}`} review={review} />}
          />
        </div>

        <div className='am-results-cta am-results-cta-over-wave'>
          <button className='am-chip am-chip-primary' data-open-quote>Request a free quote</button>
        </div>
      </div>
      {/* Bottom wave (DOM-rendered) */}
      <div className='am-wave am-wave-bottom' aria-hidden>
        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 120' preserveAspectRatio='none'>
          <path fill='#ffffff' d='M0,48 C360,90 720,0 1440,48 L1440,120 L0,120 Z' />
        </svg>
      </div>
    </section>
  );
}
